import React, { useState } from "react"
import { BUTTON_STYLES, COLORS } from "../constants"
import { createButtonStyle } from "../utils"

const KeywordSearch = ({
  searchInput,
  setSearchInput,
  onAddKeyword,
  addingKeyword,
  selectedCountry,
  selectedLanguage,
  onGenerateKeywords,
  disabled = false,
  keywordCount,
  keywordLimit,
  planLoading,
  onLimitReached,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const handleKeyDown = e => {
    if (e.key === "Enter" && selectedCountry && selectedLanguage) {
      onAddKeyword()
    }
  }

  const isDisabled = addingKeyword || !selectedCountry || !selectedLanguage || disabled
  const isLimitReached = keywordLimit !== 'Unlimited' && keywordCount >= parseInt(keywordLimit)
  
  console.log('KeywordSearch Debug:', { keywordCount, keywordLimit, isLimitReached })
  
  const handleInputClick = () => {
    if (isLimitReached && onLimitReached) {
      console.log('Limit reached, triggering upgrade modal')
      onLimitReached()
    }
  }
  
  const getPlaceholderText = () => {
    if (isLimitReached) {
      return `Keyword limit reached (${keywordLimit}/${keywordLimit})`
    }
    if (!selectedCountry && !selectedLanguage) {
      return "Select country and language first"
    } else if (!selectedCountry) {
      return "Select a country first"
    } else if (!selectedLanguage) {
      return "Select a language first"
    }
    return "Enter the keyword"
  }

  const buttonStyle = createButtonStyle(
    isHovered,
    {
      ...BUTTON_STYLES.addButton,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: "10px 20px",
      minWidth: "120px",
      height: "40px",
      fontSize: "14px",
      fontWeight: "500",
      opacity: (isDisabled || isLimitReached) ? 0.5 : 1,
      cursor: (isDisabled || isLimitReached) ? "not-allowed" : "pointer",
      pointerEvents: (isDisabled || isLimitReached) ? "none" : "auto",
      zIndex: 10,
      position: "relative",
    },
    (isDisabled || isLimitReached) ? "#64748b" : COLORS.primary,
    (isDisabled || isLimitReached) ? "#475569" : COLORS.primaryHover
  )

  return (
    <div className="d-flex flex-column gap-2">
      {!selectedCountry || !selectedLanguage ? (
        <div
          style={{
            fontSize: "13px",
            color: "#94a3b8",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginBottom: "8px",
          }}
        >
          <i className="fa-solid fa-info-circle" style={{ fontSize: "12px" }} />
          Please select country and language above to continue
        </div>
      ) : null}

      {/* SEARCH ROW - Responsive Layout */}
      <div
        className="d-flex gap-3"
        style={{ 
          flexDirection: window.innerWidth < 768 ? "column" : "row",
          alignItems: window.innerWidth < 768 ? "stretch" : "center"
        }}
      >
        {/* INPUT CONTAINER - Full width on small screens */}
        <div
          style={{ 
            flex: window.innerWidth < 768 ? 1 : "auto",
            width: window.innerWidth < 768 ? "100%" : "auto"
          }}
        >
          <div
            className="d-flex align-items-center"
            style={{
              flex: 1,
              minWidth: 0,
              border: "1px solid #3A3B47",
              borderRadius: "6px",
              padding: "0 10px 0 16px",
              height: "40px",
              opacity: (isDisabled || isLimitReached) ? 0.5 : 1,
              gap: "8px",
            }}
          >
            <i
              className="fas fa-magnifying-glass"
              style={{ position: "static", left: "auto", flexShrink: 0 }}
            />
            <input
              type="text"
              placeholder={getPlaceholderText()}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isDisabled || isLimitReached}
              onClick={handleInputClick}
              onFocus={handleInputClick}
              title={isLimitReached ? "Keyword limit reached. Click to upgrade your plan." : (isDisabled ? "Please select country and language first" : "")}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                fontSize: "14px",
                color: COLORS.white,
                outline: "none",
                flex: 1,
                minWidth: 0,
                height: "100%",
                cursor: isLimitReached ? "pointer" : (isDisabled ? "not-allowed" : "text"),
              }}
            />

            {/* Keyword Count Display - Show on all screens */}
            <div
              style={{
                flexShrink: 0,
                fontSize: '11px',
                color: '#6b7280',
                whiteSpace: 'nowrap',
              }}
            >
              {planLoading ? (
                'Loading...'
              ) : (
                <span>
                  <strong>{keywordCount}</strong> / <strong>{keywordLimit}</strong> KW
                </span>
              )}
            </div>

            {/* AI Generate Button inside input - always show */}
            <button
              onClick={onGenerateKeywords}
              disabled={isDisabled || isLimitReached}
              title={
                isLimitReached ? "Keyword limit reached. Click to upgrade your plan." :
                (isDisabled ? "Select country and language first" : "Generate AI-powered keywords")
              }
              style={{
                flexShrink: 0,
                background: (isDisabled || isLimitReached) ? '#4b5563' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: COLORS.white,
                border: 'none',
                borderRadius: '6px',
                padding: '8px 14px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: (isDisabled || isLimitReached) ? 'not-allowed' : 'pointer',
                opacity: (isDisabled || isLimitReached) ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap',
                boxShadow: (isDisabled || isLimitReached) ? 'none' : '0 2px 8px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              <i
                className="fas fa-magic"
                style={{ fontSize: '10px' }}
              />
              AI Keywords
            </button>
          </div>
        </div>

        {/* ADD BUTTON - Only show inline on desktop */}
        {window.innerWidth >= 768 && (
          <button
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onAddKeyword}
            disabled={isDisabled || isLimitReached}
            title={
              isLimitReached ? "Keyword limit reached. Click to upgrade your plan." :
              (isDisabled ? "Select country and language first" : "Add keyword")
            }
            style={{
              ...buttonStyle,
              padding: "10px 20px",
              minWidth: "120px",
              maxWidth: "150px",
              height: "40px",
              flexShrink: 0,
              fontSize: "14px"
            }}
          >
            <span>Add</span>
            {addingKeyword && (
              <i
                className="fa fa-spinner fa-spin"
                style={{
                  fontSize: "16px",
                  color: COLORS.white,
                  marginLeft: "8px",
                }}
              />
            )}
          </button>
        )}
      </div>

      {/* ADD AND IMPORT BUTTONS ROW - Only on small screens */}
      {window.innerWidth < 768 && (
        <div
          className="d-flex gap-2 mt-3"
          style={{ justifyContent: "center" }}
        >
          {/* Add Button */}
          <button
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onAddKeyword}
            disabled={isDisabled || isLimitReached}
            title={
              isLimitReached ? "Keyword limit reached. Click to upgrade your plan." :
              (isDisabled ? "Select country and language first" : "Add keyword")
            }
            style={{
              ...buttonStyle,
              padding: "10px 20px",
              minWidth: "120px",
              height: "40px",
              fontSize: "14px",
              flex: 1
            }}
          >
            <span>Add</span>
            {addingKeyword && (
              <i
                className="fa fa-spinner fa-spin"
                style={{
                  fontSize: "16px",
                  color: COLORS.white,
                  marginLeft: "8px",
                }}
              />
            )}
          </button>

          {/* Import Button - Icon Only */}
          <button
            onClick={() => {
              // This will be handled by the parent component
              if (window.onImportClick) {
                window.onImportClick()
              }
            }}
            disabled={isDisabled || isLimitReached}
            title={isLimitReached ? "Keyword limit reached. Click to upgrade your plan." : "Import Keywords"}
            style={{
              backgroundColor: (isDisabled || isLimitReached) ? '#4b5563' : COLORS.primary,
              color: COLORS.white,
              padding: "10px",
              borderRadius: "4px",
              border: "none",
              cursor: (isDisabled || isLimitReached) ? 'not-allowed' : 'pointer',
              fontSize: "14px",
              width: "40px",
              height: "40px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: (isDisabled || isLimitReached) ? 0.6 : 1,
              transition: "all 0.2s ease",
              flexShrink: 0
            }}
          >
            <i
              className="fas fa-upload"
              style={{ fontSize: "12px" }}
            ></i>
          </button>
        </div>
      )}
    </div>
  )
}

export default KeywordSearch
